import type { I18n, TranslationKey } from "./i18n/i18n";
import type { OverlayHandle } from "./app_overlays";
import { drawPlayer } from "./render/entity_drawers";
import type { SnapshotEntity } from "./render/types";
import { drawDamageZone, drawOrbitingBall, drawProjectile } from "./render/weapon_drawers";

export type UpgradeOverlayOptions = {
  title: string;
  upgrades: unknown[];
  i18n: I18n;
  playerBallColor: string;
  timeoutSeconds?: number;
  onChoose: (upgrade: unknown) => void;
};

type UpgradeVisualKind =
  | "stat"
  | "projectileShot"
  | "orbitingBalls"
  | "damageZone"
  | "generic";

const DEFAULT_UPGRADE_TIMEOUT_SECONDS = 8;

export function createUpgradeOverlay(options: UpgradeOverlayOptions): OverlayHandle {
  const wrapper = document.createElement("div");
  wrapper.className = "upgrade-controls";

  const header = document.createElement("div");
  header.className = "upgrade-header";

  const title = document.createElement("h2");
  title.className = "upgrade-title";
  title.textContent = options.title;

  const countdown = document.createElement("div");
  countdown.className = "upgrade-countdown";

  header.append(title, countdown);

  const grid = document.createElement("div");
  grid.className = "upgrade-grid";

  for (const upgrade of options.upgrades) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "upgrade-card";

    const icon = createUpgradeIcon(upgrade, options.playerBallColor);

    const name = document.createElement("span");
    name.className = "upgrade-card-name";
    name.textContent = getUpgradeTitle(upgrade, options.i18n);

    const weaponLabelText = getUpgradeWeaponLabel(upgrade, options.i18n);
    const weaponLabel = document.createElement("span");
    weaponLabel.className = "upgrade-card-weapon";
    weaponLabel.textContent = weaponLabelText;

    const description = document.createElement("span");
    description.className = "upgrade-card-description";
    description.textContent = getUpgradeDescription(upgrade, options.i18n);

    if (weaponLabelText.length > 0) {
      button.append(icon, name, weaponLabel, description);
    } else {
      button.append(icon, name, description);
    }

    button.addEventListener("click", () => options.onChoose(upgrade));

    grid.append(button);
  }

  wrapper.append(header, grid);

  const endTime =
    Date.now() + Math.max(1, options.timeoutSeconds ?? DEFAULT_UPGRADE_TIMEOUT_SECONDS) * 1000;

  let countdownTimer: number | null = null;

  const updateCountdown = () => {
    const secondsLeft = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
    countdown.textContent = `${secondsLeft}s`;

    if (secondsLeft === 0 && countdownTimer !== null) {
      window.clearInterval(countdownTimer);
      countdownTimer = null;
    }
  };

  updateCountdown();
  countdownTimer = window.setInterval(updateCountdown, 200);

  return {
    element: wrapper,
    destroy: () => {
      if (countdownTimer !== null) {
        window.clearInterval(countdownTimer);
        countdownTimer = null;
      }

      wrapper.remove();
    }
  };
}

function createUpgradeIcon(upgrade: unknown, playerBallColor: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const ratio = window.devicePixelRatio || 1;
  const width = 78;
  const height = 58;

  canvas.className = "upgrade-card-icon";
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.style.display = "block";
  canvas.style.margin = "0 auto 8px";

  const context = canvas.getContext("2d");

  if (!context) {
    return canvas;
  }

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);

  const kind = getUpgradeVisualKind(upgrade);

  if (kind === "projectileShot") {
    drawProjectileShotIcon(context, width, height);
  } else if (kind === "orbitingBalls") {
    drawOrbitingBallsIcon(context, width, height);
  } else if (kind === "damageZone") {
    drawDamageZoneIcon(context, width, height);
  } else {
    drawStatIcon(context, width, height, playerBallColor);
  }

  return canvas;
}

function drawProjectileShotIcon(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  drawProjectile(context, width * 0.48, height * 0.5, 13, { x: 1, y: 0 });
}

function drawOrbitingBallsIcon(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  drawOrbitingBall(context, width * 0.5, height * 0.5, 14);
}

function drawDamageZoneIcon(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  drawDamageZone(context, width * 0.5, height * 0.5, 23);
}

function drawStatIcon(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  playerBallColor: string
): void {
  drawPlayer(
    context,
    width * 0.5,
    height * 0.5,
    18,
    createIconEntity(playerBallColor),
    null
  );
}

function createIconEntity(color: string): SnapshotEntity {
  return {
    id: "upgrade-icon",
    kind: "player",
    position: {
      x: 0,
      y: 0
    },
    radius: 18,
    cosmetics: {
      ball_color: hexToRgb(color),
      name_color: {
        r: 255,
        g: 255,
        b: 255
      }
    }
  };
}

function getUpgradeTitle(upgrade: unknown, i18n: I18n): string {
  if (!upgrade || typeof upgrade !== "object") {
    return t(i18n, "upgrade.generic.title");
  }

  const value = upgrade as Record<string, unknown>;

  if ("Stat" in value) {
    return getStatTitle(String(value.Stat), i18n);
  }

  if ("NewWeapon" in value) {
    return getWeaponTitle(String(value.NewWeapon), i18n);
  }

  if ("WeaponUpgrade" in value) {
    const payload = value.WeaponUpgrade as Record<string, unknown>;
    return getWeaponUpgradeTitle(String(payload.upgrade), i18n);
  }

  return t(i18n, "upgrade.generic.title");
}

function getUpgradeWeaponLabel(upgrade: unknown, i18n: I18n): string {
  if (!upgrade || typeof upgrade !== "object") {
    return "";
  }

  const value = upgrade as Record<string, unknown>;

  if ("WeaponUpgrade" in value) {
    const payload = value.WeaponUpgrade as Record<string, unknown>;
    return getWeaponTitle(String(payload.weapon), i18n);
  }

  return "";
}

function getUpgradeDescription(upgrade: unknown, i18n: I18n): string {
  if (!upgrade || typeof upgrade !== "object") {
    return t(i18n, "upgrade.generic.description");
  }

  const value = upgrade as Record<string, unknown>;

  if ("Stat" in value) {
    return getStatDescription(String(value.Stat), i18n);
  }

  if ("NewWeapon" in value) {
    return getWeaponDescription(String(value.NewWeapon), i18n);
  }

  if ("WeaponUpgrade" in value) {
    const payload = value.WeaponUpgrade as Record<string, unknown>;
    return getWeaponUpgradeDescription(String(payload.upgrade), i18n);
  }

  return t(i18n, "upgrade.generic.description");
}

function getStatTitle(stat: string, i18n: I18n): string {
  if (stat === "MaxHp") {
    return t(i18n, "upgrade.stat.maxHp.title");
  }

  if (stat === "Speed") {
    return t(i18n, "upgrade.stat.speed.title");
  }

  return t(i18n, "upgrade.stat.generic.title");
}

function getStatDescription(stat: string, i18n: I18n): string {
  if (stat === "MaxHp") {
    return t(i18n, "upgrade.stat.maxHp.description");
  }

  if (stat === "Speed") {
    return t(i18n, "upgrade.stat.speed.description");
  }

  return t(i18n, "upgrade.stat.generic.description");
}

function getWeaponTitle(weapon: string, i18n: I18n): string {
  if (weapon === "BasicShot" || weapon === "ProjectileShot") {
    return t(i18n, "upgrade.weapon.projectileShot.title");
  }

  if (weapon === "OrbitingBalls") {
    return t(i18n, "upgrade.weapon.orbitingBalls.title");
  }

  if (weapon === "DamageZone") {
    return t(i18n, "upgrade.weapon.damageZone.title");
  }

  return t(i18n, "upgrade.weapon.generic.title");
}

function getWeaponDescription(weapon: string, i18n: I18n): string {
  if (weapon === "BasicShot" || weapon === "ProjectileShot") {
    return t(i18n, "upgrade.weapon.projectileShot.description");
  }

  if (weapon === "OrbitingBalls") {
    return t(i18n, "upgrade.weapon.orbitingBalls.description");
  }

  if (weapon === "DamageZone") {
    return t(i18n, "upgrade.weapon.damageZone.description");
  }

  return t(i18n, "upgrade.weapon.generic.description");
}

function getWeaponUpgradeTitle(upgrade: string, i18n: I18n): string {
  if (upgrade === "Frequency") {
    return t(i18n, "upgrade.weaponUpgrade.frequency.title");
  }

  if (upgrade === "Range") {
    return t(i18n, "upgrade.weaponUpgrade.range.title");
  }

  if (upgrade === "ProjectileCount") {
    return t(i18n, "upgrade.weaponUpgrade.projectileCount.title");
  }

  if (upgrade === "ProjectileSpeed") {
    return t(i18n, "upgrade.weaponUpgrade.projectileSpeed.title");
  }

  if (upgrade === "OrbitalSpeed") {
    return t(i18n, "upgrade.weaponUpgrade.orbitalSpeed.title");
  }

  if (upgrade === "Damage") {
    return t(i18n, "upgrade.weaponUpgrade.damage.title");
  }

  return t(i18n, "upgrade.weaponUpgrade.generic.title");
}

function getWeaponUpgradeDescription(upgrade: string, i18n: I18n): string {
  if (upgrade === "Frequency") {
    return t(i18n, "upgrade.weaponUpgrade.frequency.description");
  }

  if (upgrade === "Range") {
    return t(i18n, "upgrade.weaponUpgrade.range.description");
  }

  if (upgrade === "ProjectileCount") {
    return t(i18n, "upgrade.weaponUpgrade.projectileCount.description");
  }

  if (upgrade === "ProjectileSpeed") {
    return t(i18n, "upgrade.weaponUpgrade.projectileSpeed.description");
  }

  if (upgrade === "OrbitalSpeed") {
    return t(i18n, "upgrade.weaponUpgrade.orbitalSpeed.description");
  }

  if (upgrade === "Damage") {
    return t(i18n, "upgrade.weaponUpgrade.damage.description");
  }

  return t(i18n, "upgrade.weaponUpgrade.generic.description");
}

function getUpgradeVisualKind(upgrade: unknown): UpgradeVisualKind {
  if (!upgrade || typeof upgrade !== "object") {
    return "generic";
  }

  const value = upgrade as Record<string, unknown>;

  if ("Stat" in value) {
    return "stat";
  }

  if ("NewWeapon" in value) {
    return weaponVisualKind(String(value.NewWeapon));
  }

  if ("WeaponUpgrade" in value) {
    const payload = value.WeaponUpgrade as Record<string, unknown>;
    return weaponVisualKind(String(payload.weapon));
  }

  return "generic";
}

function weaponVisualKind(weapon: string): UpgradeVisualKind {
  if (weapon === "BasicShot" || weapon === "ProjectileShot") {
    return "projectileShot";
  }

  if (weapon === "OrbitingBalls") {
    return "orbitingBalls";
  }

  if (weapon === "DamageZone") {
    return "damageZone";
  }

  return "generic";
}

function t(i18n: I18n, key: TranslationKey): string {
  return i18n.t(key);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace("#", "");
  const value = Number.parseInt(normalized, 16);

  if (!Number.isFinite(value) || normalized.length !== 6) {
    return {
      r: 255,
      g: 255,
      b: 255
    };
  }

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}